// @ts-nocheck

import * as Sentry from '@sentry/react';
import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { v4 } from 'uuid';

import { Close } from '../../assets/svg';
import { RedirectServices, getRedirectUrl } from '../../config/urls';
import useRouter from '../../hooks/useRouter';
import { getLangLong, __ } from '../../i18n/l10n';
import { getAuthManager } from '../../managers/authManager';
import { AuthMessages } from '../../types/auth';
import { storage } from '../../utils';
import SHA256 from '../../utils/sha256';
import LoadingSpinner from '../LoadingSpinner';
import PrimaryButton from '../PrimaryButton';

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgb(238, 238, 238);
`;

const AuthApiForm = styled.form`
  display: none;
`;

const Frame = styled.iframe`
  height: 100%;
  width: 100%;
  border: none;
  ${({ $isLoaded }) =>
    !$isLoaded
      ? css`
          display: none;
        `
      : undefined}
`;

const LoaderWrapper = styled.div`
  display: ${({ $show }) => ($show ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const Error = styled.div`
  position: absolute;
  top: 0.3em;
  left: 3em;
  left: calc(3em + constant(safe-area-inset-left));
  left: calc(3em + env(safe-area-inset-left));
  right: 3em;
  right: calc(3em + constant(safe-area-inset-right));
  right: calc(3em + env(safe-area-inset-right));
  padding: 0.5em;
  background-color: #b20e0f;
  border-radius: 0.2em;
  min-height: 2em;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: normal;
  text-align: center;
`;

const Warning = styled(Error)`
  background-color: #ddd;
  color: #000;
  flex-direction: column;

  &&&& button {
    margin-top: 0.5em;
  }
`;

const CloseIcon = styled(Close)`
  position: absolute;
  top: 1em;
  left: 1em;
  width: 1.3em;
  min-width: 1.3em;
  color: #fff;
  margin: 0 0.6em;
  filter: drop-shadow(0px 0px 1px rgba(0, 0, 0, 1));
`;

const getUserLogin = (): string | null => {
  const data = storage.localStorage.load('application:auth');

  if (data) {
    const { email, login } = data;

    if (login) {
      return login;
    }
    if (email) {
      return email;
    }
  }

  return null;
};

const encrypt = (str) => btoa(SHA256(str));

const Login = ({ licence, authFormUrl, setUserNickName, submitLoginJwt, openExternalLink, onClose }) => {
  const { history, location } = useRouter();

  const iframeRef = useRef();
  const formRef = useRef();

  const authChallenge = useMemo(() => v4(), []);

  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [shouldSafelyRenderEscape, setShouldSafelyRenderEscape] = useState(false);
  const triggerError = useCallback((error) => {
    setError(error);
    setIsLoaded(false);
  }, []);
  const renderInput = (name, value) => <input type="hidden" name={name} value={value || ''} />;

  const postMessage = useCallback(
    (action: string, data): void => {
      iframeRef.current.contentWindow.postMessage(
        {
          action,
          data,
        },
        authFormUrl,
      );
    },
    [authFormUrl],
  );

  const renderError = useCallback(() => {
    if (error) {
      if (typeof error === 'object') {
        const { meta } = error;

        if (meta) {
          const { label, buttonLink, buttonLabel } = meta;

          if (label) {
            if (buttonLink && buttonLabel) {
              return (
                <Warning>
                  {label}{' '}
                  <PrimaryButton>
                    <a href={buttonLink}>{buttonLabel}</a>
                  </PrimaryButton>
                </Warning>
              );
            }
            return <Error>{label}</Error>;
          }
          console.error(
            'warning: check SubmissionError. Should be either an object with meta (containing a label) or a string.',
          );
        }
        console.error('warning: check SubmissionError. Should be either an object with meta or a string.');
        return null;
      }
      return <Error>{error}</Error>;
    }
    return null;
  }, [error]);

  const onAuthorizationGranted = useCallback(
    async ({ nickname, authorization_code, login }) => {
      try {
        storage.localStorage.save('application:auth', { login });

        setUserNickName(nickname);
        const { jwt, expirationDelay, error } =
          (await getAuthManager().generateToken(authorization_code, authChallenge)) ?? {};

        if (jwt) {
          try {
            await submitLoginJwt(jwt, expirationDelay, licence);
            history.goBackOrReplace(location.pathname);
          } catch (e) {
            console.log(e);
            Sentry.captureException(e);
            // eslint-disable-next-line sonarjs/no-duplicate-string
            triggerError(e?.errors?._error || __('General problem'));
          }
        } else {
          triggerError(__(error) || __('General problem'));
        }
      } catch (err) {
        console.log(err);
        triggerError(__('General problem'));
      }
    },
    [authChallenge, history, licence, location.pathname, setUserNickName, submitLoginJwt, triggerError],
  );

  const onMessage = useCallback(
    (message: unknown) => {
      const { data: messageData, origin } = message;

      if (!origin || authFormUrl.indexOf(origin) !== 0) {
        // Origin incorrect
        return;
      }

      if (messageData) {
        const type = messageData.type;
        const target = messageData.target;

        if (target === AuthMessages.LOADED) {
          const data = {
            login: getUserLogin(),
            isRememberChecked: false,
          };

          if (iframeRef.current) {
            postMessage('fillInput', data);
          }

          setIsLoaded(true);
        } else if (type === 'redirect') {
          const { data } = messageData;

          if (target === AuthMessages.FORGOTPASSWORD) {
            const forgottenPasswordUrl = getRedirectUrl(RedirectServices.LOST_PASSWORD, { must_logout: 1 });
            openExternalLink(forgottenPasswordUrl, { delay: 300 });
          } else if (target === AuthMessages.REGISTER) {
            const registerUrl = getRedirectUrl(RedirectServices.REGISTER, { mobile: 1 });
            openExternalLink(registerUrl, { delay: 300, auth: true });
          } else if (data && data.authorization_code) {
            onAuthorizationGranted(data);
          }
        } else if (type === AuthMessages.SUBMITTED) {
          setError(null);
        }
      }
    },
    [authFormUrl, onAuthorizationGranted, openExternalLink, postMessage],
  );

  useEffect(() => {
    window.addEventListener('message', onMessage);

    if (!isLoaded) {
      formRef.current.submit();
    }

    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [authFormUrl, onMessage, error, isLoaded]);

  useEffect(() => {
    const pid = setTimeout(() => {
      setShouldSafelyRenderEscape(true);
    }, 2000);

    return () => {
      if (pid) {
        clearTimeout(pid);
      }
    };
  }, []);

  const shouldRenderEscape = useMemo(
    () => onClose && (shouldSafelyRenderEscape || isLoaded),
    [onClose, shouldSafelyRenderEscape, isLoaded],
  );

  // Dans un premier temps, on utilise le formulaire de type web-poker pour avoir le meme form que le poker.
  // Quand on aura fait le redesign de l'application, on verra pour changer en web-betting avec le nouveau design.
  return (
    <Wrapper>
      <>
        <AuthApiForm action={authFormUrl} target="login" method="POST" ref={formRef}>
          {renderInput('challenge_hash', encrypt(authChallenge))}
          {renderInput('application', 'mobile-betting')}
          {renderInput('platform', 'web')}
          {renderInput('trust_level', 3)}
          {renderInput('lang', getLangLong())}
          {renderInput('oid', global?.getTracker ? global.getTracker() : '')}
          <input type="submit" />
        </AuthApiForm>

        {renderError()}

        <Frame name="login" ref={iframeRef} $isLoaded={isLoaded} />
        <LoaderWrapper $show={isLoaded}>
          <LoadingSpinner />
        </LoaderWrapper>
        {shouldRenderEscape ? <CloseIcon onClick={onClose} /> : null}
      </>
    </Wrapper>
  );
};

export default Login;
